import React, { useState, useEffect } from 'react';
import { Key, Eye, Trash2, Users, LogIn, LogOut, CheckCircle, AlertCircle, Plus, Server, Edit2, Copy, Shield, Ban } from 'lucide-react';
import dockerHubService from '../services/dockerHubService';
import registryService from '../services/registryService';
import * as apiKeyService from '../services/apiKeyService';
import { useAuth } from '../hooks/useAuth';

const ApiKeysSection = () => {
  const { isInferenceOnly, isAdmin } = useAuth();
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [creating, setCreating] = useState(false);
  const [keyForm, setKeyForm] = useState({
    name: '',
    is_account_key: false,
    scope_account: false,
    scope_inference: true,
    scope_catalog: false,
    allowed_models: '',
    spending_limit_cents: '',
    spending_period: '',
  });

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    try {
      const res = await apiKeyService.listKeys();
      if (res.success) setKeys(res.keys || []);
    } catch (err) {
      console.error('Failed to fetch API keys:', err);
    } finally {
      setKeysLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        name: keyForm.name,
        is_account_key: keyForm.is_account_key,
        scope_account: keyForm.scope_account,
        scope_inference: keyForm.scope_inference,
        scope_catalog: keyForm.scope_catalog,
      };
      if (!keyForm.is_account_key) {
        if (keyForm.allowed_models.trim()) {
          payload.allowed_models = keyForm.allowed_models.split(',').map(m => m.trim()).filter(Boolean);
        }
        if (keyForm.spending_limit_cents) {
          payload.spending_limit_cents = parseInt(keyForm.spending_limit_cents);
          payload.spending_period = keyForm.spending_period || 'month';
        }
      }
      const res = await apiKeyService.createKey(payload);
      if (res.success) {
        setNewKeyResult(res.key);
        setShowCreateForm(false);
        setKeyForm({ name: '', is_account_key: false, scope_account: false, scope_inference: true, scope_catalog: false, allowed_models: '', spending_limit_cents: '', spending_period: '' });
        await fetchKeys();
      } else {
        alert(res.error || 'Failed to create key');
      }
    } catch (err) {
      alert('Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId) => {
    if (!window.confirm('Revoke this API key? It will stop working immediately.')) return;
    await apiKeyService.revokeKey(keyId);
    await fetchKeys();
  };

  const handleDelete = async (keyId) => {
    if (!window.confirm('Permanently delete this API key?')) return;
    await apiKeyService.deleteKey(keyId);
    await fetchKeys();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow border">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">API Keys</h3>
          <p className="text-sm text-gray-500 mt-1">Use API keys to access TCF inference endpoints programmatically.</p>
        </div>
        {!showCreateForm && !newKeyResult && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
          >
            <Plus size={16} />
            <span>Create Key</span>
          </button>
        )}
      </div>

      {/* New key result — shown ONCE */}
      {newKeyResult && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <CheckCircle size={20} className="text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-green-800">API Key Created: {newKeyResult.name}</p>
              <p className="text-sm text-green-700 mt-1">Copy this key now. You won't be able to see it again.</p>
              <div className="mt-2 flex items-center space-x-2">
                <code className="bg-white border rounded px-3 py-2 text-sm font-mono flex-1 break-all">{newKeyResult.raw_key}</code>
                <button
                  onClick={() => copyToClipboard(newKeyResult.raw_key)}
                  className="p-2 text-green-700 hover:bg-green-100 rounded"
                  title="Copy"
                >
                  <Copy size={16} />
                </button>
              </div>
              <button
                onClick={() => setNewKeyResult(null)}
                className="mt-2 text-sm text-green-700 underline"
              >
                I've copied my key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create form */}
      {showCreateForm && (
        <form onSubmit={handleCreate} className="mb-4 p-4 bg-gray-50 rounded-lg border space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Key Name</label>
            <input
              type="text"
              value={keyForm.name}
              onChange={(e) => setKeyForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Production, MCP Integration"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              required
            />
          </div>
          {/* Account keys and account scope are withheld from inference-only
              accounts: such a key authenticates against every non-inference
              route, which would be a way around the account's role. The backend
              forces the scope down regardless (routes/apikey.js). */}
          {isInferenceOnly ? (
            <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              <Shield size={14} className="mt-0.5 shrink-0" />
              <span>
                Keys on this account are scoped to inference — the playground and prediction
                endpoints. Account access is not available.
              </span>
            </div>
          ) : (
            <>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_account_key"
                  checked={keyForm.is_account_key}
                  onChange={(e) => setKeyForm(prev => ({ ...prev, is_account_key: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="is_account_key" className="text-sm text-gray-700 flex items-center space-x-1">
                  <Shield size={14} />
                  <span>Account Key (unrestricted access to all models, no spending limit)</span>
                </label>
              </div>
              <div className="pl-4 space-y-2 border-l-2 border-gray-200">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Access Scopes</p>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="scope_account"
                    checked={keyForm.scope_account}
                    onChange={(e) => setKeyForm(prev => ({ ...prev, scope_account: e.target.checked }))}
                    className="rounded"
                  />
                  <label htmlFor="scope_account" className="text-sm text-gray-700">
                    Account access <span className="text-gray-400">— clusters, billing, images, registries, settings</span>
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="scope_inference"
                    checked={keyForm.scope_inference}
                    onChange={(e) => setKeyForm(prev => ({ ...prev, scope_inference: e.target.checked }))}
                    className="rounded"
                  />
                  <label htmlFor="scope_inference" className="text-sm text-gray-700">
                    Inference access <span className="text-gray-400">— playground and prediction endpoints</span>
                  </label>
                </div>
                {isAdmin && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="scope_catalog"
                      checked={keyForm.scope_catalog}
                      onChange={(e) => setKeyForm(prev => ({ ...prev, scope_catalog: e.target.checked }))}
                      className="rounded"
                    />
                    <label htmlFor="scope_catalog" className="text-sm text-gray-700">
                      Catalog access <span className="text-gray-400">— read-only GPU catalog and prices across providers. Can expose un-margined supplier cost, so admin only.</span>
                    </label>
                  </div>
                )}
              </div>
            </>
          )}
          {!keyForm.is_account_key && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Allowed Models (comma-separated, leave empty for all)</label>
                <input
                  type="text"
                  value={keyForm.allowed_models}
                  onChange={(e) => setKeyForm(prev => ({ ...prev, allowed_models: e.target.value }))}
                  placeholder="e.g. llama-3.1-8b, mistral-7b"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Spending Limit (credits)</label>
                  <input
                    type="number"
                    value={keyForm.spending_limit_cents}
                    onChange={(e) => setKeyForm(prev => ({ ...prev, spending_limit_cents: e.target.value }))}
                    placeholder="e.g. 1000"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
                  <select
                    value={keyForm.spending_period}
                    onChange={(e) => setKeyForm(prev => ({ ...prev, spending_period: e.target.value }))}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">No limit</option>
                    <option value="week">Per Week</option>
                    <option value="month">Per Month</option>
                    <option value="year">Per Year</option>
                  </select>
                </div>
              </div>
            </>
          )}
          <div className="flex space-x-2">
            <button
              type="submit"
              disabled={creating}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 text-sm disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Key'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Keys list */}
      {keysLoading ? (
        <p className="text-sm text-gray-500">Loading keys...</p>
      ) : keys.length === 0 && !showCreateForm ? (
        <div className="text-center py-6 text-gray-500">
          <Key size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">No API keys created yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map(key => (
            <div key={key.id} className={`p-3 border rounded-lg ${key.is_active ? 'hover:bg-gray-50' : 'bg-gray-50 opacity-60'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {key.is_account_key ? <Shield size={18} className="text-purple-600" /> : <Key size={18} className="text-gray-600" />}
                  <div>
                    <p className="font-medium text-sm">{key.name}</p>
                    <p className="text-xs text-gray-500 font-mono">
                      {key.key_prefix}...
                      {key.is_account_key && <span className="ml-1 text-purple-600">(account key)</span>}
                      {!key.is_active && <span className="ml-1 text-red-500">(revoked)</span>}
                    </p>
                    <div className="flex space-x-1 mt-0.5">
                      {!!key.scope_account && <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">account</span>}
                      {!!key.scope_inference && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded">inference</span>}
                      {!!key.scope_catalog && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded">catalog</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3 text-xs text-gray-500">
                  {key.spending_limit_cents && (
                    <span>{key.current_period_spent_cents}/{key.spending_limit_cents} credits ({key.spending_period})</span>
                  )}
                  {key.last_used_at && (
                    <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                  )}
                  {key.is_active && (
                    <button onClick={() => handleRevoke(key.id)} className="text-orange-500 hover:text-orange-700 p-1" title="Revoke">
                      <Ban size={15} />
                    </button>
                  )}
                  <button onClick={() => handleDelete(key.id)} className="text-red-500 hover:text-red-700 p-1" title="Delete">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { isInferenceOnly } = useAuth();
  const [dockerHubCredentials, setDockerHubCredentials] = useState({
    username: '',
    password: ''
  });
  const [dockerHubStatus, setDockerHubStatus] = useState({
    isAuthenticated: false,
    username: null,
    loading: false,
    error: null
  });

  // Registry credentials state
  const [registries, setRegistries] = useState([]);
  const [registriesLoading, setRegistriesLoading] = useState(true);
  const [showRegistryForm, setShowRegistryForm] = useState(false);
  const [editingRegistry, setEditingRegistry] = useState(null);
  const [registryForm, setRegistryForm] = useState({ name: '', server: '', username: '', password: '' });
  const [registryError, setRegistryError] = useState(null);
  const [registrySaving, setRegistrySaving] = useState(false);

  useEffect(() => {
    const isAuthenticated = dockerHubService.isAuthenticated();
    const username = dockerHubService.getUsername();
    setDockerHubStatus(prev => ({
      ...prev,
      isAuthenticated,
      username
    }));
    // /settings/registries is outside the inference-only allowlist, so calling
    // it would only produce a 403 and a console error.
    if (!isInferenceOnly) fetchRegistries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInferenceOnly]);

  const fetchRegistries = async () => {
    try {
      const result = await registryService.listRegistries();
      if (result.success) {
        setRegistries(result.registries || []);
      }
    } catch (err) {
      console.error('Failed to fetch registries:', err);
    } finally {
      setRegistriesLoading(false);
    }
  };

  const handleDockerHubLogin = async (e) => {
    e.preventDefault();
    setDockerHubStatus(prev => ({ ...prev, loading: true, error: null }));

    try {
      const result = await dockerHubService.login(
        dockerHubCredentials.username,
        dockerHubCredentials.password
      );

      if (result.success) {
        setDockerHubStatus({
          isAuthenticated: true,
          username: dockerHubCredentials.username,
          loading: false,
          error: null
        });
        setDockerHubCredentials({ username: '', password: '' });
      } else {
        setDockerHubStatus(prev => ({
          ...prev,
          loading: false,
          error: result.error
        }));
      }
    } catch (error) {
      setDockerHubStatus(prev => ({
        ...prev,
        loading: false,
        error: 'Login failed'
      }));
    }
  };

  const handleDockerHubLogout = async () => {
    await dockerHubService.logout();
    setDockerHubStatus({
      isAuthenticated: false,
      username: null,
      loading: false,
      error: null
    });
  };

  const handleRegistrySubmit = async (e) => {
    e.preventDefault();
    setRegistrySaving(true);
    setRegistryError(null);

    try {
      let result;
      if (editingRegistry) {
        result = await registryService.updateRegistry(editingRegistry.id, registryForm);
      } else {
        result = await registryService.createRegistry(registryForm);
      }

      if (result.success) {
        await fetchRegistries();
        setShowRegistryForm(false);
        setEditingRegistry(null);
        setRegistryForm({ name: '', server: '', username: '', password: '' });
      } else {
        setRegistryError(result.error);
      }
    } catch (err) {
      setRegistryError(err.message);
    } finally {
      setRegistrySaving(false);
    }
  };

  const handleEditRegistry = (reg) => {
    setEditingRegistry(reg);
    setRegistryForm({
      name: reg.name,
      server: reg.server,
      username: reg.username,
      password: '••••••••'
    });
    setShowRegistryForm(true);
    setRegistryError(null);
  };

  const handleDeleteRegistry = async (id) => {
    if (!window.confirm('Remove this registry credential?')) return;
    try {
      await registryService.deleteRegistry(id);
      await fetchRegistries();
    } catch (err) {
      console.error('Failed to delete registry:', err);
    }
  };

  const handleCancelRegistryForm = () => {
    setShowRegistryForm(false);
    setEditingRegistry(null);
    setRegistryForm({ name: '', server: '', username: '', password: '' });
    setRegistryError(null);
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Settings</h2>

      <div className="grid gap-6">
        {/* Docker Hub and Container Registries are for building and deploying
            images — neither is reachable by an inference-only account. */}
        {!isInferenceOnly && (
          <>
        {/* Docker Hub */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <h3 className="text-lg font-semibold mb-4">Docker Hub Integration</h3>

          {dockerHubStatus.isAuthenticated ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-green-50">
                <div className="flex items-center space-x-3">
                  <CheckCircle size={20} className="text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Connected to Docker Hub</p>
                    <p className="text-sm text-green-600">Logged in as: {dockerHubStatus.username}</p>
                  </div>
                </div>
                <button
                  onClick={handleDockerHubLogout}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
                >
                  <LogOut size={16} />
                  <span>Disconnect</span>
                </button>
              </div>
              <p className="text-sm text-gray-600">
                Your Docker Hub account is connected. You can now browse and manage your repositories in the Images section.
              </p>
            </div>
          ) : (
            <form onSubmit={handleDockerHubLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Docker Hub Username
                </label>
                <input
                  type="text"
                  value={dockerHubCredentials.username}
                  onChange={(e) => setDockerHubCredentials(prev => ({
                    ...prev,
                    username: e.target.value
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Your Docker Hub username"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password or Access Token
                </label>
                <input
                  type="password"
                  value={dockerHubCredentials.password}
                  onChange={(e) => setDockerHubCredentials(prev => ({
                    ...prev,
                    password: e.target.value
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Your Docker Hub password or access token"
                  required
                />
              </div>

              {dockerHubStatus.error && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-3 rounded-lg">
                  <AlertCircle size={16} />
                  <span className="text-sm">{dockerHubStatus.error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={dockerHubStatus.loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <LogIn size={16} />
                <span>{dockerHubStatus.loading ? 'Connecting...' : 'Connect Docker Hub'}</span>
              </button>

              <p className="text-sm text-gray-600">
                Connect your Docker Hub account to browse and manage your repositories directly from TCF Studio.
                We recommend using an access token instead of your password for better security.
              </p>
            </form>
          )}
        </div>

        {/* Container Registries */}
        <div className="bg-white p-6 rounded-lg shadow border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold">Container Registries</h3>
              <p className="text-sm text-gray-500 mt-1">
                Add credentials for private registries (Scaleway, AWS ECR, GCR, etc.). These are automatically injected when launching clusters with images from these registries.
              </p>
            </div>
            {!showRegistryForm && (
              <button
                onClick={() => { setShowRegistryForm(true); setEditingRegistry(null); setRegistryForm({ name: '', server: '', username: '', password: '' }); setRegistryError(null); }}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 text-sm"
              >
                <Plus size={16} />
                <span>Add Registry</span>
              </button>
            )}
          </div>

          {showRegistryForm && (
            <form onSubmit={handleRegistrySubmit} className="mb-4 p-4 bg-gray-50 rounded-lg border space-y-3">
              <h4 className="font-medium text-sm text-gray-700">
                {editingRegistry ? 'Edit Registry' : 'Add Registry'}
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                  <input
                    type="text"
                    value={registryForm.name}
                    onChange={(e) => setRegistryForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Scaleway Paris"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Registry Server</label>
                  <input
                    type="text"
                    value={registryForm.server}
                    onChange={(e) => setRegistryForm(prev => ({ ...prev, server: e.target.value }))}
                    placeholder="e.g. rg.fr-par.scw.cloud"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm font-mono"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
                  <input
                    type="text"
                    value={registryForm.username}
                    onChange={(e) => setRegistryForm(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="e.g. nologin"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Password / Secret Key</label>
                  <input
                    type="password"
                    value={registryForm.password}
                    onChange={(e) => setRegistryForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={editingRegistry ? '(unchanged)' : 'Enter secret key'}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    required={!editingRegistry}
                  />
                </div>
              </div>

              {registryError && (
                <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-2 rounded">
                  <AlertCircle size={14} />
                  <span className="text-sm">{registryError}</span>
                </div>
              )}

              <div className="flex space-x-2">
                <button
                  type="submit"
                  disabled={registrySaving}
                  className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50"
                >
                  {registrySaving ? 'Saving...' : editingRegistry ? 'Update' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={handleCancelRegistryForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {registriesLoading ? (
            <p className="text-sm text-gray-500">Loading registries...</p>
          ) : registries.length === 0 && !showRegistryForm ? (
            <div className="text-center py-6 text-gray-500">
              <Server size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No registries configured yet.</p>
              <p className="text-xs text-gray-400 mt-1">Add a registry to use private Docker images when launching clusters.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {registries.map(reg => (
                <div key={reg.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-3">
                    <Server size={18} className="text-purple-600" />
                    <div>
                      <p className="font-medium text-sm">{reg.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{reg.server} &middot; {reg.username}</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditRegistry(reg)}
                      className="text-gray-500 hover:text-purple-600 p-1"
                      title="Edit"
                    >
                      <Edit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDeleteRegistry(reg.id)}
                      className="text-gray-500 hover:text-red-600 p-1"
                      title="Remove"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}

        {/* API Keys */}
        <ApiKeysSection />

      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Organization</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization name
            </label>
            <input type="text" defaultValue="Mon Organisation" className="w-full border border-gray-300 rounded-lg px-3 py-2" />
          </div>
          <button className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2">
            <Users size={16} />
            <span>Invite Members</span>
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow border">
        <h3 className="text-lg font-semibold mb-4">Notifications</h3>
        <div className="space-y-3">
          {[
            'Out of credit',
            'New credit received',
            'Payment in progress',
            'Payment failed',
            'Job failed after N attempts',
            'Node failure',
            'Out of storage',
            'Job completed',
            'No compute node found after X time'
          ].map(notification => (
            <div key={notification} className="flex items-center justify-between py-2">
              <span className="text-sm">{notification}</span>
              <input type="checkbox" defaultChecked className="rounded" />
            </div>
          ))}
        </div>
      </div>
      </div>
    </div>
  );
};

export default SettingsPage;
